import React, { useState, useEffect, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Form } from 'react-bootstrap';
import Helpers from 'utils/helpers';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import SiteContext from 'components/shared/siteContext/siteContext';
import Heading from 'components/common/heading/heading';
import Panel from 'components/common/panel/panel';
import Spinner from 'components/common/spinner/spinner';
import { createTimeout, isTimeoutCanceled, cancelTimeout } from 'utils/timeout';
import AppContext from 'contexts/appContext';
import BackofficeReportsService from 'services/backofficeReportsService';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import ReportSelection from 'components/navigation/backofficeReports/common/reportSelection';
import ReportPeriod from 'components/navigation/backofficeReports/common/reportPeriod';
import useForm from 'hooks/form';
import moment from 'moment';
import _ from 'lodash';
import './backofficeReportsRunPage.css';

import image_pdf from 'assets/images/file-pdf.png';
import image_error from 'assets/images/file-error.png';

export default function BackofficeReportsRunPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: false });

  const [isLoaded, setIsLoaded] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [secondsUntilComplete, setSecondsUntilComplete] = useState(0);
  const [secondsSinceTimeChange, setSecondsSinceTimeChange] = useState(0);
  const [isSlideoutOpen, setIsSlideourOpen] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);
  const [runs, setRuns] = useState([]);
  const [hierarchyId, setHierarchyId] = useState();
  const [selectedReportTypeId, setSelectedReportTypeId] = useState('');
  const [isPeriodRelative, setIsPeriodRelative] = useState(true);
  const [relativePeriodValue, setRelativePeriodSelectedValue] = useState('');
  const [relativeOffsetStartValue, setRelativeOffsetStartValue] = useState();
  const [relativeOffsetEndValue, setRelativeOffsetEndValue] = useState();
  const [configurationId, setConfigurationId] = useState('');
  const [absolutePeriodStartValue, setAbsolutePeriodStartSelectedValue] = useState();
  const [absolutePeriodEndValue, setAbsolutePeriodEndSelectedValue] = useState();
  const [jobOptions, setJobOptions] = useState([]);
  const [savedId, setSavedId] = useState(false);
  const [timeoutNum, setTimeoutNum] = useState();
  const [timeoutCount, setTimeoutCount] = useState(0);
  const [types, setTypes] = useState([]);

  const initialTimeoutPeriod = 10000;
  const periodicTimeoutPeriod = 2000;

  useAsyncEffect(async () => {
    setAppArea(AppContext.Areas.BackofficeReports, 'Run Report');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Backoffice Reports', selected: true }]);
    setHierarchyId(await BackofficeReportsHelper.getSiteContextHierarchy());
    setTypes(await BackofficeReportsHelper.getReportTypes());
    setRuns(await BackofficeReportsHelper.getUserRuns());
    setIsLoaded(true);
  }, [hierarchyId]);

  useEffect(() => {
    return () => {
      cancelTimeout(timeoutNum);
    };
  }, [timeoutNum]);

  const handleSaveTaskClicked = submitForm(async (cancelSubmit) => {
    if (!hierarchyId) {
      setHierarchyError('A store must be selected using the control at the top of the page.')
      return cancelSubmit();
    }
    const taskId = await BackofficeReportsService.API.insertTaskForRun(null, {
      hierarchyId: hierarchyId,
      reportTypeId: selectedReportTypeId,
      configurationId: configurationId,
      isRelativeDate: isPeriodRelative,
      relativeDateValue: relativePeriodValue == null || relativePeriodValue === '' ? undefined : relativePeriodValue,
      relativeStartOffset: relativeOffsetStartValue == null || relativeOffsetStartValue === '' ? undefined : relativeOffsetStartValue,
      relativeEndOffset: relativeOffsetEndValue == null || relativeOffsetEndValue === '' ? undefined : relativeOffsetEndValue,
      absoluteStartDate: absolutePeriodStartValue == null || absolutePeriodStartValue === '' ? undefined : absolutePeriodStartValue,
      absoluteEndDate: absolutePeriodEndValue == null || absolutePeriodEndValue === '' ? undefined : absolutePeriodEndValue,
      jobOptions: jobOptions,
    });
    props.history.push(`/BackofficeReports/Task/Edit/${taskId}`);
  });

  const handleRunNowClicked = submitForm(async (cancelSubmit) => {
    if (!hierarchyId) {
      setHierarchyError('A store must be selected using the control at the top of the page.')
      return cancelSubmit();
    }
    setIsRunning(true);
    let taskId = await BackofficeReportsService.API.insertTaskForRun(savedId, {
      hierarchyId: hierarchyId,
      reportTypeId: selectedReportTypeId,
      configurationId: configurationId,
      isRelativeDate: isPeriodRelative,
      relativeDateValue: relativePeriodValue == null || relativePeriodValue === '' ? undefined : relativePeriodValue,
      relativeStartOffset: relativeOffsetStartValue == null || relativeOffsetStartValue === '' ? undefined : relativeOffsetStartValue,
      relativeEndOffset: relativeOffsetEndValue == null || relativeOffsetEndValue === '' ? undefined : relativeOffsetEndValue,
      absoluteStartDate: absolutePeriodStartValue == null || absolutePeriodStartValue === '' ? undefined : absolutePeriodStartValue,
      absoluteEndDate: absolutePeriodEndValue == null || absolutePeriodEndValue === '' ? undefined : absolutePeriodEndValue,
      jobOptions: jobOptions,
    });
    setSavedId(taskId);
    executeNowAndDownload(taskId);
  });

  const executeNowAndDownload = async (id) => {
    const runId = await BackofficeReportsService.API.executeTaskNow(id);

    const startCheckingForDocumentDownload = async (timeoutId) => {
      const run = await BackofficeReportsService.API.getTaskRunStatus(id, runId);
      if (isTimeoutCanceled(timeoutId)) {
        return;
      }
      if (run.runStatus === 0) {
        setTimeoutNum(createTimeout(startCheckingForDocumentDownload, periodicTimeoutPeriod));
        setTimeoutCount(timeoutCount + 1);
        if (run.secondsUntilComplete === secondsUntilComplete) {
          setSecondsSinceTimeChange(secondsSinceTimeChange + (initialTimeoutPeriod / 1000));
        }
        else {
          setSecondsSinceTimeChange(0);
        }
        setSecondsUntilComplete(run.secondsUntilComplete);
      }
      else {
        setRuns([run, ...runs]);
        setIsRunning(false);
      }
    };

    const timeoutId = createTimeout(startCheckingForDocumentDownload, initialTimeoutPeriod);
    setTimeoutNum(timeoutId);
    setTimeoutCount(0);
    setSecondsSinceTimeChange(initialTimeoutPeriod / 1000);
    setSecondsUntilComplete(0);
  };

  const handleSelectSite = (id) => {
    BackofficeReportsHelper.setSiteContextHierarchy(id);
    cancelTimeout(timeoutNum);
    setHierarchyId(id);
    setHierarchyError(null);
    setIsRunning(false);
  };

  const onChangeReportType = (newReportTypeId, newReportType) => {
    setSelectedReportTypeId(newReportTypeId);
    const relDateOptions = BackofficeReportsHelper.getRelativeDateOptions(newReportType);
    if (_.findIndex(relDateOptions, { key: relativePeriodValue }) < 0) {
      if (relDateOptions && relDateOptions.length > 0) {
        onChangeRelativePeriod(_.find(relDateOptions, { default: true }).key);
      }
      else {
        setRelativePeriodSelectedValue(null);
        setRelativeOffsetStartValue(null);
        setRelativeOffsetEndValue(null);
      }
    }

    const absDateOptions = BackofficeReportsHelper.getAbsoluteDateOptions(newReportType);
    if (absDateOptions == null) {
      setAbsolutePeriodStartSelectedValue(null);
      setAbsolutePeriodEndSelectedValue(null);
    }
    else if (absDateOptions.length === 1) {
      if (absolutePeriodStartValue == null) {
        setAbsolutePeriodStartSelectedValue(new Date());
        setAbsolutePeriodEndSelectedValue(new Date());
      }
      else {
        setAbsolutePeriodEndSelectedValue(absolutePeriodStartValue);
      }
    }
    else {
      if (absolutePeriodStartValue == null) {
        setAbsolutePeriodStartSelectedValue(new Date());
        setAbsolutePeriodEndSelectedValue(new Date());
      }
      else if (absolutePeriodEndValue == null) {
        setAbsolutePeriodEndSelectedValue(absolutePeriodStartValue);
      }
    }
  };

  const onChangeRelativePeriod = (newRelativePeriod) => {
    setRelativePeriodSelectedValue(newRelativePeriod);
    if (newRelativePeriod === 'daysoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else if (newRelativePeriod === 'daysrangeoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(2);
    }
    else if (newRelativePeriod === 'weeksoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else {
      setRelativeOffsetStartValue(null);
      setRelativeOffsetEndValue(null);
    }
  };

  const onChangeAbsolutePeriodStartValue = (value) => {
    setAbsolutePeriodStartSelectedValue(value);
    setAbsolutePeriodEndSelectedValue(value);
  };

  const onChangeAbsolutePeriodEndValue = (value) => {
    setAbsolutePeriodEndSelectedValue(value);
  };

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <SiteContext.Button loaded={isLoaded} hierarchyId={hierarchyId} onSelectSite={handleSelectSite} selectableFn={BackofficeReportsHelper.siteFilterFn} />
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='secondary' onClick={handleRunNowClicked} iconClass='icons8-start' label='Run Now' loading={isRunning} loadingLabel='Running...' />
            <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save As Task' />
          </div>
        </Heading>
      </Flow.Header>
      <Flow.Body className={'backoffice-reports-run ' + (isSlideoutOpen ? 'expanded' : 'collapsed')}>
        <div className='d-flex flex-grow-1 mh-100'>
          <Flow className='form-holder flex-grow-1'>
            <Flow.Body>
              <div className='row justify-content-center mx-0 mt-4'>
                <div className='col-12 col-md-11 col-xl-10' style={{ maxWidth: 800 }}>
                  <Form ref={setFormRef} noValidate>
                    <Panel>
                      <Panel.Header>Run Report</Panel.Header>
                      <Panel.Body>
                        {hierarchyError && <div className='invalid-feedback d-block mb-2 mx-0'>{hierarchyError}</div>}
                        <ReportSelection
                          hierarchyId={hierarchyId}
                          types={types}
                          selectedReportTypeId={selectedReportTypeId}
                          onChangeReportType={onChangeReportType}
                          configurationId={configurationId}
                          onChangeConfigurationId={setConfigurationId}
                          jobOptions={jobOptions}
                          onChangeJobOptions={setJobOptions}
                        />
                        <ReportPeriod
                          types={types}
                          selectedReportTypeId={selectedReportTypeId}
                          relativePeriodValue={relativePeriodValue}
                          onChangeRelativePeriod={onChangeRelativePeriod}
                          relativeOffsetStartValue={relativeOffsetStartValue}
                          setRelativeOffsetStartValue={setRelativeOffsetStartValue}
                          relativeOffsetEndValue={relativeOffsetEndValue}
                          setRelativeOffsetEndValue={setRelativeOffsetEndValue}
                          absolutePeriodStartValue={absolutePeriodStartValue}
                          onChangeAbsolutePeriodStartValue={onChangeAbsolutePeriodStartValue}
                          absolutePeriodEndValue={absolutePeriodEndValue}
                          onChangeAbsolutePeriodEndValue={onChangeAbsolutePeriodEndValue}
                          isPeriodRelative={isPeriodRelative}
                          setIsPeriodRelative={setIsPeriodRelative}
                        />
                      </Panel.Body>
                      <Panel.Footer>
                        {renderFormValidationMessage()}
                        <Inputs.Button variant='secondary' onClick={handleRunNowClicked} iconClass='icons8-start' label='Run Now' loading={isRunning} loadingLabel='Running...' />
                        <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save As Task' />
                      </Panel.Footer>
                    </Panel>
                  </Form>
                </div>
              </div>
            </Flow.Body>
          </Flow>
          <Flow className='slideout-holder flex-grow-0'>
            <Flow.Body>
              <Slideout title='Reports' open={isSlideoutOpen} onToggleOpen={() => setIsSlideourOpen(!isSlideoutOpen)}>
                {isRunning ? <RunProgressRow secondsUntilComplete={secondsUntilComplete} isCalculating={timeoutCount === 0} /> : null}
                {runs.map((item, index) => (item.runStatus < 0 ? RunErrorRow(item, index) : RunHistoryRow(item, index)))}
                {!isRunning && runs.length === 0 ? <div className='text-center slideout-show-expanded'>No reports have been run</div> : null}
              </Slideout>
            </Flow.Body>
          </Flow>
          {!isSlideoutOpen ? null : <div className='fade backdrop show' onClick={() => setIsSlideourOpen(false)}></div>}
        </div>
      </Flow.Body>
    </Flow >
  );
}

function Slideout(props) {
  const handleToggleOpen = () => {
    if (props.onToggleOpen) {
      props.onToggleOpen();
    }
  };

  return (
    <div className={Helpers.String.join(['slideout', 'd-flex', 'mh-100', props.className])}>
      <Flow>
        <Flow.Header>
          <div className='slideout-handle' >
            <Inputs.Button variant='default' size='lg' iconClass={props.open ? 'icons8-chevron-right' : 'icons8-chevron-left'} circle onClick={() => handleToggleOpen()} />
          </div>
          <div className='slideout-title'>
            {props.title}
          </div>
        </Flow.Header>
        <Flow.Body className='slideout-body'>
          {props.children}
        </Flow.Body>
      </Flow>

    </div>
  );
}

function RunHistoryRow(taskRunData, index) {
  const onRowClicked = () => {
    /*ShowMediaPopup({
      title: 'Daily Sales Report - 10/31/2019',
      content:
        <iframe src='{taskRunData.fileURL}' title='PDF' height='100%' width='100%' style={{ height: '100%', width: '100%', display: 'block', borderWidth: 0 }}>An error occured.</iframe>
    });*/
    window.open(taskRunData.fileURL, '_blank').focus();
  };

  return (
    <div className='item-active w-100 lh-120' onClick={onRowClicked} key={index}>
      <div className='slideout-show-collapsed text-center m-2'>
        <img src={image_pdf} alt='PDF' style={{ height: 28 }} />
      </div>
      <div className='slideout-show-expanded px-3 py-2 border-bottom'>
        <div className='d-flex justify-content-start align-items-center'>
          <img src={image_pdf} alt='PDF' style={{ height: 28 }} />
          <div className='ml-2 d-inline-block align-middle'>
            <span className='text-link'>
              <span>{taskRunData.reportName}</span>
              <span className='small ml-1'>({taskRunData.configurationName})</span>
            </span>
            <div className='text-link my-1'>
              <span>{taskRunData.requestedDateStr}</span>
              <span className='small ml-1'>
                {(taskRunData.reportDateStr && taskRunData.reportDateStr.length > 0 && taskRunData.reportDateStr !== taskRunData.requestedDateStr) ? '(' + taskRunData.reportDateStr + ')' : ''}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div className='ml-1 status'>Generated {moment(taskRunData.actualRunDateTime).format('ddd, MMM D, YYYY h:mm:ssa')}</div>
        </div>
      </div>
    </div>
  );
}

function RunErrorRow(taskRunData, index) {
  return (
    <div className='w-100 lh-120' key={index}>
      <div className='slideout-show-collapsed text-center m-2'>
        <img src={image_error} alt='ERROR' style={{ height: 26 }} />
      </div>
      <div className='slideout-show-expanded px-3 py-2 border-bottom'>
        <div className='d-flex justify-content-start align-items-center'>
          <img src={image_error} alt='ERROR' style={{ height: 26 }} />
          <div className='ml-2 d-inline-block align-middle'>
            <span className='text-dark'>
              <span>{taskRunData.reportName}</span>
              <span className='small ml-1'>({taskRunData.configurationName})</span>
            </span>
            <div className='my-1'>
              <span>{taskRunData.requestedDateStr}</span>
              <span className='small ml-1'>
                {(taskRunData.reportDateStr && taskRunData.reportDateStr.length > 0 && taskRunData.reportDateStr !== taskRunData.requestedDateStr) ? '(' + taskRunData.reportDateStr + ')' : ''}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div className='ml-1 mb-1 text-danger status'>{taskRunData.errorMessage}</div>
          <div className='ml-1 status'>Attemped {moment(taskRunData.actualRunDateTime).format('ddd, MMM D, YYYY h:mm:ssa')}</div>
        </div>
      </div>
    </div>
  );
}

function RunProgressRow(props) {
  return (
    <div className='w-100 lh-120'>
      <div className='slideout-show-collapsed text-center m-2'>
        <Spinner animation='circular' style={{ height: 24, width: 24, verticalAlign: 'middle' }} />
      </div>
      <div className='slideout-show-expanded px-3 py-2 border-bottom'>
        <div className='d-flex justify-content-start align-items-center'>
          <div style={{ lineHeight: '28px' }}>
            <Spinner animation='circular' style={{ height: 24, width: 24, verticalAlign: 'middle' }} />
            <span className='ml-3 font-weight-bold'>In Progress</span>
          </div>
        </div>
        <div>
          <span style={{ display: 'inline-block', width: 24 }}></span>
          <span className='ml-3 status'>
            {props.isCalculating ?
              'Calculating time remaining...'
              : (
                props.secondsUntilComplete > 0
                  ? `Approximate time remaining: ${moment.duration(props.secondsUntilComplete, 'seconds').humanize()}`
                  : 'This may take a few moments...'
              )
            }
          </span>
        </div>
      </div>
    </div>
  );
}